import React, { useEffect, useState, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import pricingService from '../../services/pricingService';
import Button from '../../components/common/Button';
import { useToast } from '../../hooks/useToast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTags,
  faPlus,
  faEdit,
  faTrash,
  faCoins,
  faCheck,
  faBan,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/PricingRules.css';

export default function PricingRules() {
  const { showToast } = useToast();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentRule, setCurrentRule] = useState({
    action: '',
    cost: 1.0,
    description: '',
    enabled: true,
  });

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await pricingService.getPricingRules();
      const data = res?.data?.data || [];
      setRules(data);
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to load pricing rules', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const stats = useMemo(() => {
    const total = rules.length;
    const active = rules.filter((r) => r.enabled).length;
    const defaults = rules.filter((r) => r.is_default && !r.updated_at).length;
    const overrides = rules.filter((r) => r.updated_at || !r.is_default).length;
    return { total, active, defaults, overrides };
  }, [rules]);

  const handleOpenCreate = () => {
    setIsEdit(false);
    setCurrentRule({
      action: '',
      cost: 1.0,
      description: '',
      enabled: true,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (rule) => {
    setIsEdit(true);
    setCurrentRule({
      action: rule.action,
      cost: rule.cost,
      description: rule.description || '',
      enabled: rule.enabled,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentRule.action.trim()) {
      showToast('Action identifier is required', 'error');
      return;
    }
    if (currentRule.cost < 0) {
      showToast('Cost must be 0 or greater', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        cost: parseFloat(currentRule.cost),
        description: currentRule.description.trim(),
        enabled: Boolean(currentRule.enabled),
      };
      await pricingService.upsertPricingRule(currentRule.action.trim().toUpperCase(), payload);
      showToast(
        `Pricing rule for '${currentRule.action.toUpperCase()}' saved successfully`,
        'success'
      );
      setModalOpen(false);
      fetchRules();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to save pricing rule', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDelete = (rule) => {
    setRuleToDelete(rule);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;
    setSubmitting(true);
    try {
      await pricingService.deletePricingRule(ruleToDelete.action);
      showToast(
        `Rule override for '${ruleToDelete.action}' removed successfully`,
        'success'
      );
      setDeleteModalOpen(false);
      setRuleToDelete(null);
      fetchRules();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to remove rule override', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="pricing-rules-container">
        {/* Top Header */}
        <div className="pricing-rules-header">
          <div>
            <h1 className="pricing-rules-title">
              <FontAwesomeIcon icon={faTags} className="pricing-rules-title-icon" />
              Platform Pricing Rules
            </h1>
            <p className="pricing-rules-subtitle">
              Configure credit costs for user actions. Custom rules override built-in platform defaults.
            </p>
          </div>
          <Button onClick={handleOpenCreate} variant="primary">
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
            Add Rule / Override
          </Button>
        </div>

        {/* Stats Row */}
        <div className="pricing-stats-grid">
          <div className="pricing-stat-card">
            <div className="pricing-stat-label">Total Rules</div>
            <div className="pricing-stat-value">{stats.total}</div>
          </div>
          <div className="pricing-stat-card">
            <div className="pricing-stat-label">Active Rules</div>
            <div className="pricing-stat-value active">{stats.active}</div>
          </div>
          <div className="pricing-stat-card">
            <div className="pricing-stat-label">Platform Defaults</div>
            <div className="pricing-stat-value defaults">{stats.defaults}</div>
          </div>
          <div className="pricing-stat-card">
            <div className="pricing-stat-label">Custom Overrides</div>
            <div className="pricing-stat-value overrides">{stats.overrides}</div>
          </div>
        </div>

        {/* Rules Table / Empty State */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>Loading pricing rules...</div>
        ) : rules.length === 0 ? (
          <div className="pricing-empty-state">
            <FontAwesomeIcon icon={faTags} className="pricing-empty-icon" />
            <h3>No Pricing Rules Found</h3>
            <p className="pricing-rules-subtitle" style={{ marginBottom: '1.5rem' }}>
              Click below to create your first pricing rule.
            </p>
            <Button onClick={handleOpenCreate} variant="primary">Add Rule</Button>
          </div>
        ) : (
          <div className="pricing-table-wrapper">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>ACTION KEY</th>
                  <th>CREDIT COST</th>
                  <th>STATUS</th>
                  <th>RULE SOURCE</th>
                  <th>DESCRIPTION</th>
                  <th className="pricing-actions-col">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => {
                  const hasCustomOverride = Boolean(rule.updated_at || !rule.is_default);
                  return (
                    <tr key={rule.action}>
                      <td>
                        <code className="pricing-action-code">{rule.action}</code>
                      </td>
                      <td>
                        <span className="pricing-cost-display">
                          <FontAwesomeIcon icon={faCoins} className="pricing-coin-icon" />
                          {rule.cost} <span className="pricing-unit-text">credits</span>
                        </span>
                      </td>
                      <td>
                        <span className={`pricing-badge ${rule.enabled ? 'active' : 'disabled'}`}>
                          <FontAwesomeIcon icon={rule.enabled ? faCheck : faBan} style={{ fontSize: '0.7rem' }} />
                          {rule.enabled ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td>
                        <span className={`pricing-badge ${hasCustomOverride ? 'override' : 'default'}`}>
                          {hasCustomOverride ? 'Custom Override' : 'System Default'}
                        </span>
                      </td>
                      <td className="pricing-desc-col">{rule.description || '—'}</td>
                      <td className="pricing-actions-col">
                        <div className="pricing-btn-group">
                          <button
                            onClick={() => handleOpenEdit(rule)}
                            title="Edit Rule"
                            className="pricing-edit-btn"
                          >
                            <FontAwesomeIcon icon={faEdit} /> Edit
                          </button>
                          {hasCustomOverride && (
                            <button
                              onClick={() => handleOpenDelete(rule)}
                              title={rule.is_default ? 'Revert to System Default' : 'Delete Rule'}
                              className="pricing-delete-btn"
                            >
                              <FontAwesomeIcon icon={rule.is_default ? faRotateLeft : faTrash} />
                              {rule.is_default ? 'Revert' : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Create / Edit Modal */}
        {modalOpen && (
          <div className="pricing-modal-overlay" onClick={() => setModalOpen(false)}>
            <div className="pricing-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3 className="pricing-modal-title">
                {isEdit ? `Edit Rule: ${currentRule.action}` : 'Create Pricing Rule'}
              </h3>
              <form onSubmit={handleSubmit} className="pricing-form">
                <div>
                  <label className="pricing-field-label">Action Identifier *</label>
                  <input
                    required
                    disabled={isEdit}
                    placeholder="e.g. PLACE_ORDER, CREATE_AUCTION"
                    value={currentRule.action}
                    onChange={(e) =>
                      setCurrentRule({ ...currentRule, action: e.target.value.toUpperCase().replace(/\s+/g, '_') })
                    }
                    className="pricing-input mono"
                  />
                  {!isEdit && (
                    <span className="pricing-helper-text">
                      Standard actions: PLACE_ORDER, CREATE_AUCTION
                    </span>
                  )}
                </div>

                <div>
                  <label className="pricing-field-label">Credit Cost *</label>
                  <input
                    required
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="1.0"
                    value={currentRule.cost}
                    onChange={(e) => setCurrentRule({ ...currentRule, cost: e.target.value })}
                    className="pricing-input"
                  />
                </div>

                <div>
                  <label className="pricing-field-label">Description</label>
                  <textarea
                    rows={2}
                    placeholder="Brief description of the action cost..."
                    value={currentRule.description}
                    onChange={(e) => setCurrentRule({ ...currentRule, description: e.target.value })}
                    className="pricing-textarea"
                  />
                </div>

                <div className="pricing-checkbox-row">
                  <input
                    type="checkbox"
                    id="rule-enabled"
                    checked={currentRule.enabled}
                    onChange={(e) => setCurrentRule({ ...currentRule, enabled: e.target.checked })}
                    className="pricing-checkbox"
                  />
                  <label htmlFor="rule-enabled" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                    Rule Enabled (if disabled, action is free or falls back to system policy)
                  </label>
                </div>

                <div className="pricing-modal-actions">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="pricing-cancel-btn"
                  >
                    Cancel
                  </button>
                  <Button type="submit" disabled={submitting} variant="primary">
                    {submitting ? 'Saving...' : isEdit ? 'Update Rule' : 'Create Rule'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete / Revert Confirmation Modal */}
        {deleteModalOpen && ruleToDelete && (
          <div className="pricing-modal-overlay" onClick={() => setDeleteModalOpen(false)}>
            <div className="pricing-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3 className="pricing-modal-title danger">
                {ruleToDelete.is_default ? 'Revert Rule to Default?' : 'Delete Pricing Rule?'}
              </h3>
              <p className="pricing-rules-subtitle" style={{ lineHeight: 1.5 }}>
                {ruleToDelete.is_default ? (
                  <>
                    Are you sure you want to remove the custom override for{' '}
                    <code className="pricing-action-code">{ruleToDelete.action}</code>? The action cost will revert to the platform
                    default.
                  </>
                ) : (
                  <>
                    Are you sure you want to delete the rule for{' '}
                    <code className="pricing-action-code">{ruleToDelete.action}</code>? This action cannot be undone.
                  </>
                )}
              </p>
              <div className="pricing-modal-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={() => setDeleteModalOpen(false)}
                  className="pricing-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleConfirmDelete}
                  className="pricing-confirm-danger-btn"
                >
                  {submitting ? 'Processing...' : ruleToDelete.is_default ? 'Revert to Default' : 'Delete Rule'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
